import {
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { type ReactNode, useState } from "react";
import { useEditor } from "../store.js";
import { type DragData, createDefaultWidget } from "./types.js";

interface DndProviderProps {
  children: ReactNode;
}

export function DndProvider({ children }: DndProviderProps) {
  const insertWidget = useEditor((s) => s.insertWidget);
  const reorderWithinColumn = useEditor((s) => s.reorderWithinColumn);
  const moveWidgetBetweenColumns = useEditor((s) => s.moveWidgetBetweenColumns);
  const appendWidget = useEditor((s) => s.appendWidget);
  const page = useEditor((s) => s.page);

  const [activeLabel, setActiveLabel] = useState<string | null>(null);

  // Split Mouse + Touch instead of PointerSensor — PointerSensor can
  // miss activations in some React 19 setups where synthetic pointer
  // events get re-queued. Explicit sensors are more deterministic.
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 100, tolerance: 5 },
    }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const onDragStart = (event: DragStartEvent) => {
    const data = event.active.data.current as DragData | undefined;
    if (!data) return;
    setActiveLabel(data.kind === "palette" ? `+ ${data.widgetType}` : data.widgetId);
  };

  const onDragCancel = () => setActiveLabel(null);

  const onDragEnd = (event: DragEndEvent) => {
    setActiveLabel(null);

    const { active, over } = event;
    if (!over) return;

    const activeData = active.data.current as DragData | undefined;
    const overData = over.data.current as
      | DragData
      | { kind: "column"; columnId: string }
      | undefined;
    if (!activeData) return;

    // Palette item -> canvas drop
    if (activeData.kind === "palette") {
      const widget = createDefaultWidget(activeData.widgetType);
      if (!widget) return;

      if (overData && "kind" in overData && overData.kind === "widget") {
        // Insert before the hovered widget
        const col = findColumn(page, overData.columnId);
        if (!col) return;
        const idx = col.widgets.findIndex((w) => w.id === overData.widgetId);
        insertWidget(overData.columnId, idx === -1 ? col.widgets.length : idx, widget);
        return;
      }
      if (overData && "kind" in overData && overData.kind === "column") {
        const col = findColumn(page, overData.columnId);
        if (!col) return;
        insertWidget(overData.columnId, col.widgets.length, widget);
        return;
      }
      // Fallback: append to first column.
      appendWidget(widget);
      return;
    }

    // Widget drag: same-column reorder OR cross-column move.
    if (activeData.kind === "widget") {
      if (overData && "kind" in overData && overData.kind === "widget") {
        const sameColumn = overData.columnId === activeData.columnId;
        if (active.id === over.id) return;

        if (sameColumn) {
          const col = findColumn(page, activeData.columnId);
          if (!col) return;
          const from = col.widgets.findIndex((w) => w.id === active.id);
          const to = col.widgets.findIndex((w) => w.id === over.id);
          if (from === -1 || to === -1) return;
          reorderWithinColumn(activeData.columnId, from, to);
          return;
        }

        // Cross-column: insert at the hovered widget's position in the
        // target column (pushes that widget down).
        const targetCol = findColumn(page, overData.columnId);
        if (!targetCol) return;
        const toIdx = targetCol.widgets.findIndex((w) => w.id === overData.widgetId);
        moveWidgetBetweenColumns(
          String(active.id),
          activeData.columnId,
          overData.columnId,
          toIdx === -1 ? targetCol.widgets.length : toIdx,
        );
        return;
      }

      // Dropped on a column's empty space (useDroppable zone).
      if (
        overData &&
        "kind" in overData &&
        overData.kind === "column" &&
        overData.columnId !== activeData.columnId
      ) {
        const targetCol = findColumn(page, overData.columnId);
        if (!targetCol) return;
        moveWidgetBetweenColumns(
          String(active.id),
          activeData.columnId,
          overData.columnId,
          targetCol.widgets.length,
        );
      }
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragCancel={onDragCancel}
    >
      {children}
      <DragOverlay dropAnimation={null}>
        {activeLabel ? (
          <div
            style={{
              padding: "6px 12px",
              borderRadius: 6,
              background: "var(--accent)",
              color: "white",
              fontSize: 12,
              fontWeight: 500,
              boxShadow: "0 10px 30px rgba(0,0,0,0.3)",
              pointerEvents: "none",
              whiteSpace: "nowrap",
            }}
          >
            {activeLabel}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

function findColumn(page: ReturnType<typeof useEditor.getState>["page"], id: string) {
  for (const s of page.sections) {
    const col = s.columns.find((c) => c.id === id);
    if (col) return col;
  }
  return null;
}
